import React, { FC, useCallback, useState } from 'react';
import { Confirm } from '@workboard/wobo-ui';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTranslation } from 'react-i18next';
import { useAxios } from '../hooks/useAxios';
import { useNotification } from '../notifications';
import Broadcast from '../../../utils/broadcast';

interface IProps {
  aiId: string;
  onClose: () => void;
  onComplete?: () => void;
  title?: string;
}

export const DeleteAiModal: FC<IProps> = ({ onClose, onComplete, aiId, title }) => {
  const { t } = useTranslation();
  const notify = useNotification();
  const [isSubmitting, setSubmitting] = useState(false);
  const deleteAI = new Broadcast('deleteAI');
  const [, deleteAi] = useAxios(
    {
      url: 'activity/Delete',
      method: 'POST',
    },
    {
      manual: true,
    }
  );

  const modalTitle = t('Delete {{title}}',{ title });
  const titleLowerCase = `${title}`.toLowerCase();

  const handleSubmit = useCallback(async () => {
    const firstCharCaptialText = titleLowerCase.charAt(0).toUpperCase() + titleLowerCase.slice(1);
    try {
      setSubmitting(true);
      await deleteAi({
        data: {
          act_id: aiId,
          ajax: true,
        },
      });
      onComplete?.();
      setSubmitting(false);
      onClose();
      notify.success(`${t("{{firstCharCaptialText}} deleted", { firstCharCaptialText })}`);
      deleteAI.postMessage(JSON.stringify({
        deleteSuccess: true
      }));
    } catch (e) {
      setSubmitting(false);
      notify.error(
        `${t('There was a problem while deleting {{titleLowerCase}}. Please try again later.', { titleLowerCase })}`,
      );
      console.error(e);
    }
  }, [aiId, onClose, onComplete, deleteAi, notify, titleLowerCase]);

  return (
    <Confirm
      isOpen
      isDanger
      dismissLabel={t('Cancel')}
      kind="normal"
      onDismiss={onClose}
      onSubmit={handleSubmit}
      submitLabel={
        isSubmitting ? (
          <CircularProgress
            size={20}
            style={{ color: '#fff' }}
            title={t('Deleting...')}
          />
        ) : (
          'OK'
        )

      }
      title={modalTitle}
    >
      <p>{t("Are you sure you want to delete this {{titleLowerCase}}?", {titleLowerCase})}</p>
      <p>
        <em>
          {t("The {{titleLowerCase}} will no longer be visible or editable and people in the loop will get notified", {titleLowerCase})}
        </em>
      </p>
    </Confirm>
  );
};
